import { useFormContext, useWatch } from 'react-hook-form';
import { isNumber } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { BoloPanelFormType, ConsolidatedLocationView, EventPanelFormType } from '@mark43/cad-api';
import { isValidLatLng } from '../../../location/helpers';

export type SelectedLocation = ConsolidatedLocationView & { isForcedResult?: boolean };

/**
 * Returns the selected location
 */
export const useSelectedLocation = (): SelectedLocation | null => {
    const eventForm = useFormContext<EventPanelFormType | BoloPanelFormType>();
    /**
     * We specifically watch the locationId because
     * we don't want to rerender every time we type into the event form
     * and we only want to rerender / refetch when a specific selection is made
     * (and for this reason no need to debounce)
     */
    const locationId = useWatch({
        name: 'location.id',
        control: eventForm.control,
    });

    // @ts-expect-error form type has bogus ConsolidatedLocationView
    const locationFullAddressKey = useWatch({
        name: 'location.fullAddressKey',
        control: eventForm.control,
    });

    /**
     * Since we rerender anytime the `locationId` changes,
     * this will always represent the value of the most
     * recently selected location
     */
    const [selectedLocation, setSelectedLocation] = useState<SelectedLocation | null>(
        eventForm.getValues('location') as ConsolidatedLocationView | null
    );

    useEffect(() => {
        // Anytime a location has been selected, return it
        if (isNumber(locationId) && locationId !== -1) {
            setSelectedLocation(eventForm.getValues('location') as ConsolidatedLocationView | null);
        }

        // Anytime a location has explicitly been cleared out, null out the state
        else if (locationId === null) {
            setSelectedLocation(null);
        }
    }, [locationId, setSelectedLocation, eventForm]);

    // Anytime a location has already been selected (e.g. locationId = 0)
    // but is changed (e.g. locationFullAddressKey changes),
    // then set the new location
    useEffect(() => {
        if (locationId === 0 && locationFullAddressKey) {
            setSelectedLocation(eventForm.getValues('location') as ConsolidatedLocationView | null);
        }
    }, [locationFullAddressKey, locationId, setSelectedLocation, eventForm]);

    const selectedLocationWithComputedIsForcedResult = useMemo(() => {
        return selectedLocation
            ? {
                  ...selectedLocation,
                  isForcedResult: !isValidLatLng(
                      selectedLocation.latitude,
                      selectedLocation.longitude
                  ),
              }
            : null;
    }, [selectedLocation]);

    return selectedLocationWithComputedIsForcedResult;
};
